import { adjustAllFragments, AdjustAllFragmentsOptions } from "./adjustAllFragments";
import { patchMultipleTracksInSubtitlesData } from "./helpers";
import { markOrphanVoicings, sourceCueIdsOf } from "./orphanVoicings";
import { AudioTrackData, ILang, ISubtitlesDataAll } from "./types";

export interface CascadeOverlapsOptions extends AdjustAllFragmentsOptions {
    /**
     * Язык оригинала пары: озвучка, у которой нет реплики оригинала в этой папке, молчит и
     * соседей не толкает (4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026 — её русскую
     * реплику удалили, а испанский перевод и wav остались; в видео её быть не должно). Пометка
     * ставится на копии записей и в данные не попадает. Без языка — как раньше.
     */
    sourceLang?: ILang;
}

export interface ShiftPatch {
    trackId: string;
    patch: Partial<AudioTrackData>;
}

/**
 * Раздвижка соседей после правки одной реплики: озвучки папки, наехавшие друг на друга,
 * сдвигаются вперёд (adjustAllFragments), и сдвинутые возвращаются списком — провайдер метит
 * их грязными, и автосохранение пишет их на сервер.
 *
 * Без режима «без автоправок» — прежняя автоправка (сдвигается и то, что ближе 50 мс к
 * предыдущей озвучке). В режиме «без автоправок» провайдер зовёт её с `onlyOverlaps`: наложения
 * снимаются всегда (правило владельца 01.10.2026 «ты должен всегда сдвигать соседние реплики
 * иначе будут наслоения»), но только сдвигом следующих озвучек ПОЗЖЕ — раньше ничего не
 * двигается, зазоры не закрываются, скорость не подгоняется. Раньше этот код стоял в провайдере
 * четырьмя одинаковыми копиями (перетаскивание, групповой сдвиг, длина, переозвучка).
 */
export function cascadeOverlaps(
    data: Partial<ISubtitlesDataAll>,
    lang: ILang,
    folder: number, // 1-based
    { sourceLang, ...options }: CascadeOverlapsOptions = {},
): { updated: Partial<ISubtitlesDataAll>; shiftPatches: ShiftPatch[] } {
    const langData = data[lang];
    const folderIndex = folder - 1;
    if (!langData || !langData[folderIndex]) {
        return { updated: data, shiftPatches: [] };
    }
    const copies = langData[folderIndex].translations.map((t) => ({ ...t }));
    const translations =
        sourceLang && sourceLang !== lang
            ? markOrphanVoicings(copies, sourceCueIdsOf(data, sourceLang, folder))
            : copies;
    const { shifted, shiftedIds } = adjustAllFragments(translations, 50, options);
    if (shiftedIds.length === 0) {
        return { updated: data, shiftPatches: [] };
    }
    const shiftPatches = shifted
        .filter((t) => shiftedIds.includes(t.id))
        .map((t) => ({
            trackId: t.id,
            patch: {
                start: t.start,
                start_rus_milli: t.start,
                end_rus_milli: t.start + t.length,
                shifted_start: t.shifted_start,
                new_start: t.new_start,
            } as Partial<AudioTrackData>,
        }));
    return {
        updated: patchMultipleTracksInSubtitlesData(data, lang, folder, shiftPatches),
        shiftPatches,
    };
}
